import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  RequestCreditsComposeMessage,
  RequestCreditsMessage,
  RequestCreditsOrders,
} from "@/types/requestCredits";
import { userLoggedOut } from "../auth/authSlice";
import { ErrorInfoSchema } from "@api/core/logistics/sales-orders/sales-orders.schema";

type RequestCreditsState = {
  orders: RequestCreditsOrders[];
  message: RequestCreditsMessage | undefined;
  composeMessage: RequestCreditsComposeMessage;
  current_page_num: number;
  success: boolean;
  errorInfo: ErrorInfoSchema | undefined;
};

const initialState: RequestCreditsState = {
  orders: [],
  message: undefined,
  composeMessage: {
    to_email: "",
    to_provider: "",
    brand_name: "",
    subject: "",
    message: "",
  },
  current_page_num: 0,
  success: false,
  errorInfo: undefined,
};

type SetRequestCreditsOrdersPayload = {
  orders: RequestCreditsOrders[];
};

type SetRequestCreditsMessagePayload = {
  message: RequestCreditsMessage;
};

type SetRequestCreditsComposeMessagePayload = {
  composeMessage: RequestCreditsComposeMessage;
};

type SetRequestCreditsErrorInfoPayload = {
  errorInfo: ErrorInfoSchema;
};

export const requestCreditsSlice = createSlice({
  name: "requestCredits",
  initialState,
  reducers: {
    setRequestCreditsOrders: (
      state,
      action: PayloadAction<SetRequestCreditsOrdersPayload>
    ) => {
      state.orders = action.payload.orders;
    },
    setRequestCreditsMessage: (
      state,
      action: PayloadAction<SetRequestCreditsMessagePayload>
    ) => {
      state.message = action.payload.message;
    },
    setRequestCreditsComposeMessage: (
      state,
      action: PayloadAction<SetRequestCreditsComposeMessagePayload>
    ) => {
      state.composeMessage = action.payload.composeMessage;
    },
    setRequestCreditsCurrentPageNum: (state, action: PayloadAction<number>) => {
      state.current_page_num = action.payload;
    },
    setRequestCreditsSuccess: (state, action: PayloadAction<boolean>) => {
      state.success = action.payload;
    },
    setRequestCreditsErrorInfo: (state, action: PayloadAction<SetRequestCreditsErrorInfoPayload>) => {
      state.errorInfo = action.payload.errorInfo;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLoggedOut, (_) => {
      return initialState;
    })
  },
});

export const {
  setRequestCreditsOrders,
  setRequestCreditsMessage,
  setRequestCreditsCurrentPageNum,
  setRequestCreditsSuccess,
  setRequestCreditsComposeMessage,
  setRequestCreditsErrorInfo,
} = requestCreditsSlice.actions;

export const selectRequestCreditOrders = (state: RootState) =>
  state.requestCredits.orders;
export const selectRequestCreditsMessage = (state: RootState) =>
  state.requestCredits.message;
export const selectRequestCreditsComposeMessage = (state: RootState) =>
  state.requestCredits.composeMessage;
export const selectRequestCreditCurrentPageNum = (state: RootState) =>
  state.requestCredits.current_page_num;
export const selectRequestCreditsSuccess = (state: RootState) =>
  state.requestCredits.success;
export const selectRequestCreditsErrorInfo = (state: RootState) =>
  state.requestCredits.errorInfo;

export default requestCreditsSlice.reducer;
